import apiClient from './api'
import { withListDefaults } from '@/lib/listFetch'
import type {
  ApiResponse,
  ApiList,
  ListQuery,
  ServiceCategory,
  ServiceCategoryRequest,
  ServiceItem,
  ServiceItemRequest,
  UpdateServiceItemRequest,
  ServiceItemSummary,
  TaxRate,
  TaxRateRequest,
  SchedulingSettings,
  SchedulingSettingsRequest,
  PublicHoliday,
  PublicHolidayRequest,
  QuoteSettings,
  QuoteSettingsRequest,
  InvoiceSettings,
  InvoiceSettingsRequest,
  DocumentTemplate,
  DocumentTemplateRequest,
  TemplatePlaceholders,
  ReusableDocument,
  ReusableDocumentRequest,
  RenameReusableDocumentRequest,
  AttachmentRule,
  AttachmentRuleRequest,
  AttachmentTarget,
  CompanySettings,
  CompanySettingsRequest,
  BrandingSettings,
  BrandingRequest,
  PdfLink,
} from '@/types'
import type { TemplateDocumentType } from '@/lib/documentType'
import type {
  OptionalFeeTemplate,
  CreateOptionalFeeTemplateRequest,
} from '@/types/billing'

// Service item create/update bodies are sent as multipart/form-data when an
// image is attached (feedback #27), otherwise as plain JSON. Laravel needs a
// POST + `_method=PUT` override for multipart updates.
type ServiceItemPayload = ServiceItemRequest | UpdateServiceItemRequest

function toServiceItemPayload(
  body: ServiceItemPayload
): ServiceItemPayload | FormData {
  if (!body.image) {
    // Strip the (absent) image key so the JSON body stays clean.
    const { image: _image, ...rest } = body
    void _image
    return rest as ServiceItemPayload
  }
  const form = new FormData()
  Object.entries(body).forEach(([key, value]) => {
    if (key === 'image') return
    if (value === undefined || value === null) return
    // Multipart stringifies everything; Laravel's `boolean` rule rejects the
    // strings "true"/"false", so send booleans (e.g. is_active) as "1"/"0".
    form.append(
      key,
      typeof value === 'boolean'
        ? value
          ? '1'
          : '0'
        : (value as string | Blob)
    )
  })
  form.append('image', body.image)
  return form
}

function serviceItemConfig(body: ServiceItemPayload) {
  return body.image
    ? { headers: { 'Content-Type': 'multipart/form-data' } }
    : undefined
}

// 1:1 with Schema_Specification Section 4.11 (settings — /settings/*).
export const settingsApi = {
  listOptionalFees: () =>
    apiClient
      .get<ApiResponse<OptionalFeeTemplate[]>>('/settings/optional-fees')
      .then(r => r.data),
  createOptionalFee: (body: CreateOptionalFeeTemplateRequest) =>
    apiClient
      .post<ApiResponse<OptionalFeeTemplate>>('/settings/optional-fees', body)
      .then(r => r.data),
  updateOptionalFee: (
    id: string,
    body: Partial<CreateOptionalFeeTemplateRequest>
  ) =>
    apiClient
      .put<
        ApiResponse<OptionalFeeTemplate>
      >(`/settings/optional-fees/${id}`, body)
      .then(r => r.data),
  deleteOptionalFee: (id: string) =>
    apiClient
      .delete<ApiResponse<null>>(`/settings/optional-fees/${id}`)
      .then(r => r.data),
  // 2.11.5 service categories
  listServiceCategories: (
    q: ListQuery & {
      is_active?: boolean
      service_items_is_active?: boolean
    } = {}
  ) => {
    const { is_active, service_items_is_active, ...rest } = q
    const params: Record<string, unknown> = { ...withListDefaults(rest) }
    if (is_active !== undefined) params.is_active = is_active ? 1 : 0
    if (service_items_is_active !== undefined) {
      params.service_items_is_active = service_items_is_active ? 1 : 0
    }
    return apiClient
      .get<ApiList<ServiceCategory>>('/settings/service-categories', {
        params,
      })
      .then(r => r.data)
  },
  createServiceCategory: (body: ServiceCategoryRequest) =>
    apiClient
      .post<ApiResponse<ServiceCategory>>('/settings/service-categories', body)
      .then(r => r.data),
  updateServiceCategory: (id: string, body: ServiceCategoryRequest) =>
    apiClient
      .put<
        ApiResponse<ServiceCategory>
      >(`/settings/service-categories/${id}`, body)
      .then(r => r.data),
  deleteServiceCategory: (id: string) =>
    apiClient
      .delete<ApiResponse<null>>(`/settings/service-categories/${id}`)
      .then(r => r.data),

  // 2.11.6 service items. The library picker uses service_category_id, while
  // the settings filter sends service_category_ids for multiple categories.
  listServiceItems: (
    q: ListQuery & {
      service_category_id?: string
      service_category_ids?: string[]
      is_active?: boolean
      paginate_by_category?: boolean
    } = {}
  ) => {
    // Strict params: send the boolean filter as 1/0 (drop it when undefined).
    const { is_active, paginate_by_category, ...rest } = q
    const params: Record<string, unknown> = { ...withListDefaults(rest) }
    if (is_active !== undefined) params.is_active = is_active ? 1 : 0
    if (paginate_by_category) params.paginate_by_category = 1
    return apiClient
      .get<ApiList<ServiceItem>>('/settings/service-items', { params })
      .then(r => r.data)
  },
  // Frequently-used catalogue items for the line "Favorite" quick-action (#28).
  // Ranked server-side by usage frequency across quote lines.
  favoriteServiceItems: (limit = 12) =>
    apiClient
      .get<ApiResponse<ServiceItem[]>>('/settings/service-items/favorites', {
        params: { limit },
      })
      .then(r => r.data),
  // 2.11.6 BR02 summary counters for the catalogue header cards.
  serviceItemsSummary: () =>
    apiClient
      .get<ApiResponse<ServiceItemSummary>>('/settings/service-items/summary')
      .then(r => r.data),
  createServiceItem: (body: ServiceItemRequest) =>
    apiClient
      .post<
        ApiResponse<ServiceItem>
      >('/settings/service-items', toServiceItemPayload(body), serviceItemConfig(body))
      .then(r => r.data),
  updateServiceItem: (id: string, body: UpdateServiceItemRequest) => {
    // Multipart can't ride a PUT through Laravel; POST with a `_method=PUT`
    // override when an image is attached, otherwise a plain JSON PUT.
    if (body.image) {
      const form = toServiceItemPayload(body) as FormData
      form.append('_method', 'PUT')
      return apiClient
        .post<
          ApiResponse<ServiceItem>
        >(`/settings/service-items/${id}`, form, serviceItemConfig(body))
        .then(r => r.data)
    }
    return apiClient
      .put<
        ApiResponse<ServiceItem>
      >(`/settings/service-items/${id}`, toServiceItemPayload(body))
      .then(r => r.data)
  },
  deleteServiceItem: (id: string) =>
    apiClient
      .delete<ApiResponse<null>>(`/settings/service-items/${id}`)
      .then(r => r.data),

  // 2.11.8 tax rates
  listTaxRates: (q: ListQuery = {}) =>
    apiClient
      .get<ApiList<TaxRate>>('/settings/tax-rates', {
        params: withListDefaults(q),
      })
      .then(r => r.data),
  // 2.11.8 BR05 active tax-rate options for line-item dropdowns. Any
  // authenticated role (operators build quotes/invoices), unlike the CRUD list.
  taxRateOptions: () =>
    apiClient
      .get<ApiResponse<TaxRate[]>>('/settings/tax-rates/options')
      .then(r => r.data),
  createTaxRate: (body: TaxRateRequest) =>
    apiClient
      .post<ApiResponse<TaxRate>>('/settings/tax-rates', body)
      .then(r => r.data),
  updateTaxRate: (id: string, body: TaxRateRequest) =>
    apiClient
      .put<ApiResponse<TaxRate>>(`/settings/tax-rates/${id}`, body)
      .then(r => r.data),
  deleteTaxRate: (id: string) =>
    apiClient
      .delete<ApiResponse<null>>(`/settings/tax-rates/${id}`)
      .then(r => r.data),

  // 2.11.9 + 2.11.11 merged — calendar & scheduling config.
  getScheduling: () =>
    apiClient
      .get<ApiResponse<SchedulingSettings>>('/settings/scheduling')
      .then(r => r.data),
  updateScheduling: (body: SchedulingSettingsRequest) =>
    apiClient
      .put<ApiResponse<SchedulingSettings>>('/settings/scheduling', body)
      .then(r => r.data),

  // 2.11.10 public holidays
  listPublicHolidays: (q: ListQuery = {}) =>
    apiClient
      .get<ApiList<PublicHoliday>>('/settings/public-holidays', {
        params: withListDefaults(q),
      })
      .then(r => r.data),
  createPublicHoliday: (body: PublicHolidayRequest) =>
    apiClient
      .post<ApiResponse<PublicHoliday>>('/settings/public-holidays', body)
      .then(r => r.data),
  updatePublicHoliday: (id: string, body: PublicHolidayRequest) =>
    apiClient
      .put<ApiResponse<PublicHoliday>>(`/settings/public-holidays/${id}`, body)
      .then(r => r.data),
  deletePublicHoliday: (id: string) =>
    apiClient
      .delete<ApiResponse<null>>(`/settings/public-holidays/${id}`)
      .then(r => r.data),

  // 2.11.12 quote settings (numbering)
  getQuoteSettings: () =>
    apiClient
      .get<ApiResponse<QuoteSettings>>('/settings/quote')
      .then(r => r.data),
  updateQuoteSettings: (body: QuoteSettingsRequest) =>
    apiClient
      .put<ApiResponse<QuoteSettings>>('/settings/quote', body)
      .then(r => r.data),

  // 2.11.13 invoice settings (numbering)
  getInvoiceSettings: () =>
    apiClient
      .get<ApiResponse<InvoiceSettings>>('/settings/invoice')
      .then(r => r.data),
  updateInvoiceSettings: (body: InvoiceSettingsRequest) =>
    apiClient
      .put<ApiResponse<InvoiceSettings>>('/settings/invoice', body)
      .then(r => r.data),

  // 2.11.14-2.11.18 document templates
  listDocumentTemplates: (
    q: ListQuery & { doc_type?: TemplateDocumentType } = {}
  ) =>
    apiClient
      .get<ApiList<DocumentTemplate>>('/settings/templates', {
        params: withListDefaults(q),
      })
      .then(r => r.data),
  createDocumentTemplate: (body: DocumentTemplateRequest) =>
    apiClient
      .post<ApiResponse<DocumentTemplate>>('/settings/templates', body)
      .then(r => r.data),
  updateDocumentTemplate: (id: string, body: DocumentTemplateRequest) =>
    apiClient
      .put<ApiResponse<DocumentTemplate>>(`/settings/templates/${id}`, body)
      .then(r => r.data),
  deleteDocumentTemplate: (id: string) =>
    apiClient
      .delete<ApiResponse<null>>(`/settings/templates/${id}`)
      .then(r => r.data),

  previewReportTemplate: (content: string) =>
    apiClient
      .post<ApiResponse<PdfLink>>('/settings/templates/preview-report', {
        content,
      })
      .then(r => r.data),
  // 2.11.14-2.11.18 placeholder palette + starter content for a doc type
  getTemplatePlaceholders: (docType: TemplateDocumentType) =>
    apiClient
      .get<
        ApiResponse<TemplatePlaceholders>
      >(`/settings/templates/placeholders/${docType}`)
      .then(r => r.data),
  // 2.11.14-2.11.18 preview any template with sample data
  previewTemplate: (body: {
    doc_type: TemplateDocumentType
    content: string
  }) =>
    apiClient
      .post<ApiResponse<PdfLink>>('/settings/templates/preview', body)
      .then(r => r.data),

  // 2.11.19 reusable documents (document library)
  listReusableDocuments: (q: ListQuery = {}) =>
    apiClient
      .get<ApiList<ReusableDocument>>('/settings/document-library', {
        params: withListDefaults(q),
      })
      .then(r => r.data),
  // Active library documents selected by attachment rules for one entity type.
  reusableDocumentOptions: (attachTo: AttachmentTarget) =>
    apiClient
      .get<
        ApiResponse<ReusableDocument[]>
      >('/settings/document-library/options', { params: { attach_to: attachTo } })
      .then(r => r.data),
  createReusableDocument: (body: ReusableDocumentRequest) => {
    const form = new FormData()
    form.append('name', body.name)
    form.append('file', body.file)
    form.append('document_type', body.document_type)
    if (body.category) form.append('category', body.category)
    // Strict params: booleans must be sent as 1/0.
    if (body.is_active !== undefined)
      form.append('is_active', body.is_active ? '1' : '0')
    return apiClient
      .post<ApiResponse<ReusableDocument>>('/settings/document-library', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then(r => r.data)
  },
  updateReusableDocument: (
    id: string,
    body: Partial<ReusableDocumentRequest>
  ) => {
    // Multipart POST + _method=PUT when a new file is attached; plain JSON
    // otherwise (e.g. toggling status). Strict params: booleans as 1/0.
    if (body.file) {
      const form = new FormData()
      if (body.name) form.append('name', body.name)
      form.append('file', body.file)
      if (body.document_type) form.append('document_type', body.document_type)
      if (body.category) form.append('category', body.category)
      if (body.is_active !== undefined)
        form.append('is_active', body.is_active ? '1' : '0')
      form.append('_method', 'PUT')
      return apiClient
        .post<
          ApiResponse<ReusableDocument>
        >(`/settings/document-library/${id}`, form, { headers: { 'Content-Type': 'multipart/form-data' } })
        .then(r => r.data)
    }
    const { file: _file, is_active, ...rest } = body
    void _file
    const payload: Record<string, unknown> = { ...rest }
    if (is_active !== undefined) payload.is_active = is_active ? 1 : 0
    return apiClient
      .put<
        ApiResponse<ReusableDocument>
      >(`/settings/document-library/${id}`, payload)
      .then(r => r.data)
  },
  renameReusableDocument: (id: string, body: RenameReusableDocumentRequest) =>
    apiClient
      .patch<
        ApiResponse<ReusableDocument>
      >(`/settings/document-library/${id}/rename`, body)
      .then(r => r.data),
  deleteReusableDocument: (id: string) =>
    apiClient
      .delete<ApiResponse<null>>(`/settings/document-library/${id}`)
      .then(r => r.data),

  // 2.11.20 attachment rules
  listAttachmentRules: (q: ListQuery = {}) =>
    apiClient
      .get<ApiList<AttachmentRule>>('/settings/attachment-rules', {
        params: withListDefaults(q),
      })
      .then(r => r.data),
  createAttachmentRule: (body: AttachmentRuleRequest) =>
    apiClient
      .post<ApiResponse<AttachmentRule>>('/settings/attachment-rules', body)
      .then(r => r.data),
  updateAttachmentRule: (id: string, body: AttachmentRuleRequest) =>
    apiClient
      .put<
        ApiResponse<AttachmentRule>
      >(`/settings/attachment-rules/${id}`, body)
      .then(r => r.data),
  deleteAttachmentRule: (id: string) =>
    apiClient
      .delete<ApiResponse<null>>(`/settings/attachment-rules/${id}`)
      .then(r => r.data),

  // 2.11.21 company settings
  getCompanySettings: () =>
    apiClient
      .get<ApiResponse<CompanySettings>>('/settings/company')
      .then(r => r.data),
  updateCompanySettings: (body: CompanySettingsRequest) =>
    apiClient
      .put<ApiResponse<CompanySettings>>('/settings/company', body)
      .then(r => r.data),

  // 2.11.22 branding
  getBranding: () =>
    apiClient
      .get<ApiResponse<BrandingSettings>>('/settings/branding')
      .then(r => r.data),
  updateBranding: (body: BrandingRequest) => {
    // Multipart can't ride a PUT through Laravel; POST with `_method=PUT` when
    // a logo file is attached (same pattern as service-item / document-library).
    if (body.logo) {
      const form = new FormData()
      form.append('logo', body.logo)
      if (body.primary_color) form.append('primary_color', body.primary_color)
      if (body.secondary_color)
        form.append('secondary_color', body.secondary_color)
      form.append('_method', 'PUT')
      return apiClient
        .post<ApiResponse<BrandingSettings>>('/settings/branding', form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then(r => r.data)
    }
    return apiClient
      .put<ApiResponse<BrandingSettings>>('/settings/branding', {
        primary_color: body.primary_color,
        secondary_color: body.secondary_color,
      })
      .then(r => r.data)
  },
}

export default settingsApi
